import { useState } from 'react';
import AdsSubTab from './performance/AdsSubTab';
import MetaSubTab from './performance/MetaSubTab';
import { MARKETING_EMAIL, pilotMailto } from '../lib/marketing';
import type { Franchisee } from '../types';

type Props = { franchisee: Franchisee | null };

type SubTabId = 'paid-ads' | 'paid-meta' | 'paid-youtube' | 'paid-tiktok' | 'paid-chatgpt';

const SUBS: ReadonlyArray<{ id: SubTabId; label: string }> = [
  { id: 'paid-ads', label: 'Google Ads' },
  { id: 'paid-meta', label: 'Meta / Facebook' },
  { id: 'paid-youtube', label: 'YouTube' },
  { id: 'paid-tiktok', label: 'TikTok' },
  { id: 'paid-chatgpt', label: 'ChatGPT' },
];

/**
 * A channel we are piloting rather than reporting on yet. Distinct from
 * PlaceholderPanel below, which says "the data isn't wired up": this one is an
 * invitation, so it has to give the reader something to do.
 */
function PilotPanel({ channel }: { channel: string }) {
  return (
    <div className="tab-placeholder">
      <h3>{channel}</h3>
      <p>
        Coming soon. If you&rsquo;d like to enroll in the pilot for {channel}, email{' '}
        <a href={pilotMailto(channel)}>{MARKETING_EMAIL}</a>.
      </p>
    </div>
  );
}

function PlaceholderPanel({ channel }: { channel: string }) {
  return (
    <div className="tab-placeholder">
      <h3>{channel}</h3>
      <p>Reporting for this channel isn't connected yet — metrics will appear here once the data source is wired up.</p>
    </div>
  );
}

export default function PaidPerformanceTab({ franchisee }: Props) {
  const [sub, setSub] = useState<SubTabId>('paid-ads');

  if (!franchisee) {
    return (
      <div className="tab-placeholder">
        <h3>Paid Performance</h3>
        <p>Select a franchisee to view their paid performance metrics.</p>
      </div>
    );
  }

  return (
    <>
      <div className="perf-sub-tabs">
        {SUBS.map((s) => (
          <button
            key={s.id}
            className={`perf-sub-btn ${sub === s.id ? 'active' : ''}`}
            onClick={() => setSub(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {sub === 'paid-ads' && <AdsSubTab af={franchisee.af_number} />}
      {sub === 'paid-meta' && <MetaSubTab af={franchisee.af_number} />}
      {sub === 'paid-youtube' && <PlaceholderPanel channel="YouTube Ads" />}
      {sub === 'paid-tiktok' && <PilotPanel channel="TikTok" />}
      {sub === 'paid-chatgpt' && <PilotPanel channel="ChatGPT" />}
    </>
  );
}
